import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '@tiptap/react';
import { buildExtensions } from './editor/extensions';
import EditorSurface from './editor/EditorSurface';
import PreviewPane from './components/PreviewPane';
import type { PdfDownloaderFn } from './components/PreviewPane';
import Ribbon from './components/ribbon/Ribbon';
import StatusBar from './components/StatusBar';
import FindReplace from './components/FindReplace';
import WordCount from './components/WordCount';
import DocsManager from './components/DocsManager';
import { useEditorStore } from './store';
import {
  listDocs,
  saveDoc,
  loadDoc,
  deleteDoc,
  getLastDocId,
  setLastDocId
} from './storage';
import { exportDocx, exportHtml, exportTxt, importFile, printDoc } from './io';
import { useShortcuts } from './shortcuts';
import './styles/index.css';

interface DraftSignaturePlacement {
  pageIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
  leftPct?: number;
  topPct?: number;
  widthPct?: number;
  heightPct?: number;
}

interface SignaturePlacementConfig {
  markAllPages: boolean;
  base: DraftSignaturePlacement;
  overrides: Record<string, DraftSignaturePlacement>;
  skippedPages?: number[];
}

interface NewLetterEditorProps {
  content: string;
  onChange: (html: string) => void;
  editable?: boolean;
  title?: string;
  qrCodeData?: string | null;
  letterNumber?: string | null;
  letterDate?: string | null;
  approvedSignatureHtml?: string | null;
  signaturePlacementConfig?: SignaturePlacementConfig | null;
  enableDraftSignatureDrag?: boolean;
  onDraftSignaturePlacementChange?: (next: DraftSignaturePlacement) => void;
  onRemoveSignaturePage?: (pageIndex: number) => void;
  registerPdfDownloader?: (fn: PdfDownloaderFn | null) => void;
}

const EMPTY_CONTENT = '<p></p>';

/**
 * Serialize HTML the same way the browser would after a DOMParser round-trip.
 * Used to recognize the parent's echo of our own onUpdate: CreateLetterPage
 * round-trips editor HTML through DOMParser before handing it back as the
 * `content` prop, so the echoed string never equals editor.getHTML() verbatim.
 * Normalizing the editor's current HTML the same way lets us detect "no real
 * change" and avoid a destructive setContent re-parse.
 */
const normalizeHtml = (html: string): string => {
  try {
    return new DOMParser().parseFromString(html, 'text/html').body.innerHTML;
  } catch {
    return html;
  }
};

export function NewLetterEditor({ content, onChange, editable = true, title: initialTitle = 'Untitled Document', qrCodeData = null, letterNumber = null, letterDate = null, approvedSignatureHtml = null, signaturePlacementConfig = null, enableDraftSignatureDrag = false, onDraftSignaturePlacementChange, onRemoveSignaturePage, registerPdfDownloader }: NewLetterEditorProps) {
  const {
    docId,
    title,
    setTitle,
    setSaveStatus,
    pageSize,
    orientation,
    margins,
    setPageSize,
    setOrientation,
    setMargins,
    newDoc,
    loadDoc: loadDocAction,
    theme,
    showFormattingMarks,
    zoom,
    zoomIn,
    zoomOut,
    zoomReset
  } = useEditorStore();

  const extensions = useMemo(() => buildExtensions(), []);
  const shellRef = useRef<HTMLDivElement>(null);
  const dirtyRef = useRef(false);
  const externalContentRef = useRef(content || EMPTY_CONTENT);
  const onChangeRef = useRef(onChange);
  const [findOpen, setFindOpen] = useState(false);
  const [wcOpen, setWcOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docs, setDocs] = useState(listDocs());

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Apply theme
  useEffect(() => {
    if (shellRef.current) {
      shellRef.current.dataset.theme = theme;
    }
  }, [theme]);

  // Toggle formatting-marks class on the document for editor CSS hooks.
  useEffect(() => {
    shellRef.current?.classList.toggle('show-marks', showFormattingMarks);
  }, [showFormattingMarks]);

  useEffect(() => {
    // Read-only instances (e.g. the off-screen viewer/approval renderer) share
    // the GLOBAL editor store; don't let them clobber the live editor's title.
    if (!editable) return;
    setTitle(initialTitle || 'Untitled Document');
  }, [initialTitle, setTitle, editable]);

  const editor = useEditor({
    extensions,
    content: content || EMPTY_CONTENT,
    editable,
    // Preserve the exact whitespace the user typed (multiple/leading spaces) so
    // a saved draft keeps its spacing when re-parsed on reopen. Without this,
    // ProseMirror collapses runs of whitespace on parse and the spaces vanish.
    parseOptions: { preserveWhitespace: 'full' },
    // Never autofocus a read-only instance: the off-screen viewer/approval
    // renderer would otherwise steal focus from the host page on mount.
    autofocus: editable ? 'end' : false,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      externalContentRef.current = html;
      dirtyRef.current = true;
      setSaveStatus('dirty');
      onChangeRef.current(html);
    }
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
  }, [editable, editor]);

  useEffect(() => {
    if (!editor) return;
    const nextContent = content || EMPTY_CONTENT;
    if (nextContent === externalContentRef.current) return;
    const current = editor.getHTML();
    if (nextContent === current) return;
    // Echo guard. The parent (CreateLetterPage) pipes our onUpdate HTML back in
    // as the `content` prop after running it through a DOMParser round-trip
    // (extractSignaturePlacementFromBody -> doc.body.innerHTML). That browser
    // re-serialization differs as a STRING from editor.getHTML() even when the
    // document is identical, so a raw-string guard treats the editor's own echo
    // as a fresh external change and calls setContent — which re-parses the
    // whole document on every edit and destroys in-progress table column/row
    // resize state (widths collapse to the min, row heights never commit).
    // Compare against the round-trip of the editor's CURRENT html: if they
    // match, the incoming content carries no real change and must be ignored.
    if (nextContent === normalizeHtml(current)) {
      externalContentRef.current = nextContent;
      return;
    }
    editor.commands.setContent(nextContent, false, { preserveWhitespace: 'full' });
    externalContentRef.current = nextContent;
    dirtyRef.current = false;
  }, [content, editor]);

  const doSave = () => {
    if (!editor) return;
    const now = Date.now();
    saveDoc({
      id: docId,
      title,
      content: editor.getHTML(),
      pageSize,
      orientation,
      margins,
      createdAt: loadDoc(docId)?.createdAt || now,
      updatedAt: now
    });
    setLastDocId(docId);
    setSaveStatus('saved');
    dirtyRef.current = false;
    setDocs(listDocs());
  };

  // Autosave (debounced) — ONLY for the live, editable editor. Read-only
  // instances (e.g. the off-screen viewer/approval PDF renderer) must NEVER
  // write to localStorage: they share the global store docId and would
  // otherwise overwrite the user's in-progress draft with the rendered
  // letter's content. Gating on `editable` keeps drafts safe.
  useEffect(() => {
    if (!editable) return;
    const t = setInterval(() => {
      if (dirtyRef.current) {
        doSave();
      }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, docId, title, pageSize, orientation, margins, editable]);

  // Warn on unload if dirty — only meaningful for the editable editor.
  useEffect(() => {
    if (!editable) return;
    const h = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [editable]);

  const handleAction = async (action: string) => {
    if (!editor) return;
    switch (action) {
      case 'new':
        if (dirtyRef.current && !confirm('Discard unsaved changes?')) return;
        editor.commands.setContent(EMPTY_CONTENT, false);
        externalContentRef.current = EMPTY_CONTENT;
        onChangeRef.current(EMPTY_CONTENT);
        newDoc();
        dirtyRef.current = false;
        break;
      case 'open':
        setDocsOpen(true);
        break;
      case 'import': {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.docx,.html,.htm,.txt';
        input.onchange = async () => {
          const f = input.files?.[0];
          if (!f) return;
          const name = await importFile(editor, f);
          if (name) setTitle(name);
        };
        input.click();
        break;
      }
      case 'save':
      case 'saveAs':
        if (action === 'saveAs') {
          const t = prompt('Title for new copy', title + ' (copy)');
          if (!t) return;
          newDoc();
          setTitle(t);
          setTimeout(doSave, 0);
        } else doSave();
        break;
      case 'delete':
        if (confirm(`Delete "${title}"? This cannot be undone.`)) {
          deleteDoc(docId);
          newDoc();
          editor.commands.setContent(EMPTY_CONTENT, false);
          externalContentRef.current = EMPTY_CONTENT;
          onChangeRef.current(EMPTY_CONTENT);
          setDocs(listDocs());
        }
        break;
      case 'exportDocx':
        await exportDocx(editor, title);
        break;
      case 'exportHtml':
        exportHtml(editor, title);
        break;
      case 'exportTxt':
        exportTxt(editor, title);
        break;
      case 'print':
        printDoc(editor, title);
        break;
      case 'find':
        setFindOpen(true);
        break;
      case 'wordcount':
        setWcOpen(true);
        break;
    }
  };

  useShortcuts(editor, {
    onSave: doSave,
    onPrint: () => handleAction('print'),
    onFind: () => setFindOpen(true),
    onNew: () => handleAction('new'),
    onOpen: () => setDocsOpen(true)
  }, editable);

  return (
    <div ref={shellRef} className="sepl-word-editor app" data-theme={theme}>
      <Ribbon editor={editor} onAction={handleAction} />
      <div className="split-main">
        <div className="split-pane editor-pane">
          <div className="editor-header">
            <span>Editor</span>
            <div className="editor-zoom">
              <button
                type="button"
                className="rb-btn sm"
                onClick={zoomOut}
                title="Zoom out"
                onMouseDown={(event) => event.preventDefault()}
              >−</button>
              <button
                type="button"
                className="zoom-readout"
                onClick={zoomReset}
                title="Reset zoom"
                onMouseDown={(event) => event.preventDefault()}
              >{Math.round(zoom * 100)}%</button>
              <button
                type="button"
                className="rb-btn sm"
                onClick={zoomIn}
                title="Zoom in"
                onMouseDown={(event) => event.preventDefault()}
              >+</button>
            </div>
          </div>
          <EditorSurface editor={editor} />
        </div>
        <div className="split-divider" aria-hidden="true" />
        <div className="split-pane preview-wrap">
          <PreviewPane
            editor={editor}
            qrCodeData={qrCodeData}
            letterNumber={letterNumber}
            letterDate={letterDate}
            approvedSignatureHtml={approvedSignatureHtml}
            signaturePlacementConfig={signaturePlacementConfig}
            enableDraftSignatureDrag={enableDraftSignatureDrag}
            onDraftSignaturePlacementChange={onDraftSignaturePlacementChange}
            onRemoveSignaturePage={onRemoveSignaturePage}
            registerPdfDownloader={registerPdfDownloader}
          />
        </div>
      </div>
      <StatusBar editor={editor} />

      <FindReplace editor={editor} open={findOpen} onClose={() => setFindOpen(false)} />
      <WordCount editor={editor} open={wcOpen} onClose={() => setWcOpen(false)} />
      <DocsManager
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        docs={docs}
        onNew={() => handleAction('new')}
        onLoad={(id) => {
          const d = loadDoc(id);
          if (!d || !editor) return;
          editor.commands.setContent(d.content || '', false);
          externalContentRef.current = d.content || EMPTY_CONTENT;
          onChangeRef.current(externalContentRef.current);
          loadDocAction(d.id, d.title);
          setPageSize(d.pageSize as never);
          setOrientation(d.orientation as never);
          setMargins(d.margins as never);
          setLastDocId(d.id);
          setSaveStatus('saved');
          dirtyRef.current = false;
        }}
        onDelete={(id) => {
          deleteDoc(id);
          setDocs(listDocs());
        }}
      />
    </div>
  );
}
